import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import api from "@/lib/api";
import { GlassCard, SectionLabel, GhostButton } from "@/components/primitives";
import CountUp from "@/components/CountUp";

const COLORS = ["#00F0FF", "#3B82F6", "#9D4CDD", "#10B981", "#F59E0B", "#EF4444"];
const tooltipStyle = {
  backgroundColor: "#111827",
  border: "1px solid #334155",
  borderRadius: "10px",
  color: "#FFFFFF",
  padding: "10px",
  fontSize: "13px",
  fontFamily: "IBM Plex Mono",
};
export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then((r) => setD(r.data)); }, []);

  const exportJson = () => {
    if (!d) return;
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sentinel-analytics.json"; a.click();
  };

  if (!d) return <div className="text-slate-400">Loading…</div>;
  const k = d.kpi;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <SectionLabel>Analytics</SectionLabel>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Operations <span className="gradient-text">intelligence</span></h1>
        </div>
        <GhostButton onClick={exportJson} data-testid="analytics-export">Export JSON</GhostButton>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: "MTTR (min)", v: 27 },
          { l: "Total incidents", v: k.total_incidents },
          { l: "Resolved", v: k.resolved_incidents },
          { l: "AI Confidence", v: k.ai_confidence, s: "%" },
        ].map((m, i) => (
          <GlassCard key={i} className="p-5">
            <SectionLabel>{m.l}</SectionLabel>
            <div className="mt-2 font-mono text-3xl font-semibold"><CountUp value={m.v} suffix={m.s || ""} /></div>
          </GlassCard>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="lg:col-span-2 p-6">
          <SectionLabel>Incidents — Last 14 days</SectionLabel>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.trends}>
                <defs>
                  <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} /><stop offset="100%" stopColor="#00F0FF" stopOpacity={0} /></linearGradient>
                  <linearGradient id="gb" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#9D4CDD" stopOpacity={0.5} /><stop offset="100%" stopColor="#9D4CDD" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
                <Tooltip
    contentStyle={tooltipStyle}
    itemStyle={{ color: "#FFFFFF" }}
    labelStyle={{ color: "#38BDF8", fontWeight: "bold" }}
/>
                <Area type="monotone" dataKey="incidents" stroke="#00F0FF" strokeWidth={2} fill="url(#ga)" />
                <Area type="monotone" dataKey="resolved" stroke="#9D4CDD" strokeWidth={2} fill="url(#gb)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <SectionLabel>Severity distribution</SectionLabel>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
<Pie
    data={d.severity_dist}
    dataKey="value"
    nameKey="name"
    innerRadius={50}
    outerRadius={90}
    paddingAngle={3}
    stroke="rgba(255,255,255,0.04)"
    label={({ name, percent }) =>
        `${name} ${(percent * 100).toFixed(0)}%`
    }
    labelLine={false}
    fill="#ffffff"
>                  {d.severity_dist.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip
    contentStyle={tooltipStyle}
    itemStyle={{ color: "#FFFFFF" }}
    labelStyle={{ color: "#38BDF8", fontWeight: "bold" }}
/>
                <Legend
    verticalAlign="bottom"
    align="center"
    iconType="circle"
    wrapperStyle={{
        color: "#ffffff",
        fontSize: 13,
        paddingTop: 15,
    }}
/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <GlassCard className="p-6">
          <SectionLabel>Department performance</SectionLabel>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.departments_perf}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
                <Tooltip
    contentStyle={tooltipStyle}
    itemStyle={{ color: "#FFFFFF" }}
    labelStyle={{ color: "#38BDF8", fontWeight: "bold" }}
/>
                <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />
                <Bar dataKey="resolved" fill="#00F0FF" radius={[6, 6, 0, 0]} />
                <Bar dataKey="open" fill="#9D4CDD" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <SectionLabel>AI usage by day</SectionLabel>
          <div className="h-72 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.ai_usage}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
                <Tooltip
    contentStyle={tooltipStyle}
    itemStyle={{ color: "#FFFFFF" }}
    labelStyle={{ color: "#38BDF8", fontWeight: "bold" }}
/>
                <Line type="monotone" dataKey="queries" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>

      <GlassCard className="p-6">
        <SectionLabel>Predictive forecast (next 7 days)</SectionLabel>
        <div className="h-60 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={[...Array(7)].map((_, i) => ({ d: `D+${i+1}`, forecast: 24 + Math.round(Math.sin(i) * 6 + i * 1.6), ci_high: 32 + i * 1.8, ci_low: 14 + i * 1.2 }))}>
              <defs><linearGradient id="gp" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.45} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
              <XAxis dataKey="d" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
              <Tooltip
    contentStyle={tooltipStyle}
    itemStyle={{ color: "#FFFFFF" }}
    labelStyle={{ color: "#38BDF8", fontWeight: "bold" }}
/>
              <Area dataKey="ci_high" stroke="transparent" fill="#9D4CDD" fillOpacity={0.08} />
              <Area dataKey="forecast" stroke="#3B82F6" strokeWidth={2} fill="url(#gp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>
    </div>
  );
}
